import React, { useState } from 'react';
import axios from 'axios';
import { Package, Scan, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartsConsumption = ({ user }) => {
  const [deviceId, setDeviceId] = useState('');
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [parts, setParts] = useState([]);
  const [scannedPart, setScannedPart] = useState('');
  const [loading, setLoading] = useState(false);

  const handleScanDevice = async () => {
    if (!deviceId.trim()) {
      alert('Please enter device ID');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/trc/devices/${deviceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDeviceInfo(response.data.device);
      }
    } catch (error) {
      console.error('Error fetching device:', error);
      alert('Device not found');
    }
  };

  const handleScanPart = () => {
    if (!scannedPart.trim()) {
      alert('Please scan part QR code');
      return;
    }

    // Parse QR code (format: PartCode+Supplier+PurchasePrice+PONo+GRNDate)
    const qrParts = scannedPart.split('+');
    
    const newPart = {
      qr_code: scannedPart,
      part_code: qrParts[0] || scannedPart,
      supplier: qrParts[1] || 'Unknown',
      purchase_price: qrParts[2] || '0',
      po_no: qrParts[3] || 'N/A',
      grn_date: qrParts[4] || new Date().toISOString(),
      action: 'consume' // default action
    };

    setParts([...parts, newPart]);
    setScannedPart('');
  };

  const updatePartAction = (index, action) => {
    const updatedParts = [...parts];
    updatedParts[index].action = action;
    setParts(updatedParts);
  };

  const removePart = (index) => {
    setParts(parts.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (parts.length === 0) {
      alert('Please scan at least one part');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const engineerId = user?.employee_id || 'ENG001';
      
      const response = await axios.post(
        `${API_URL}/api/trc/repair/parts/consume`,
        {
          device_id: deviceInfo.trc_device_id,
          engineer_id: engineerId,
          parts: parts
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const consumedCount = parts.filter(p => p.action === 'consume').length;
        const returnedCount = parts.filter(p => p.action === 'return').length;
        const damagedCount = parts.filter(p => p.action === 'damaged').length;
        
        alert(`Parts recorded!\nConsumed: ${consumedCount}\nReturned: ${returnedCount}\nDamaged: ${damagedCount}`);
        
        // Reset
        setParts([]);
        setDeviceInfo(null);
        setDeviceId('');
      }
    } catch (error) {
      console.error('Error recording parts:', error);
      alert('Error recording parts');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      consume: 'bg-green-100 text-green-800 border-green-300',
      return: 'bg-blue-100 text-blue-800 border-blue-300',
      damaged: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[action] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getActionIcon = (action) => {
    const icons = {
      consume: <CheckCircle className="w-5 h-5" />,
      return: <Package className="w-5 h-5" />,
      damaged: <AlertTriangle className="w-5 h-5" />
    };
    return icons[action];
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Package className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Parts Consumption Management</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Device & Part Scanning */}
          <div className="space-y-6">
            {/* Scan Device */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Scan Device</h2>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleScanDevice()}
                  placeholder="Scan Device ID or TRC ID..."
                  className="flex-1 px-4 py-3 border-2 rounded-lg"
                />
                <button
                  onClick={handleScanDevice}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                >
                  <Scan className="w-5 h-5" />
                </button>
              </div>

              {deviceInfo && (
                <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-bold">Device Found</span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div><strong>DID:</strong> {deviceInfo.did}</div>
                    <div><strong>Device:</strong> {deviceInfo.brand} {deviceInfo.model}</div>
                    <div><strong>TRC ID:</strong> {deviceInfo.trc_device_id}</div>
                    <div><strong>Status:</strong> {deviceInfo.status}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Scan Parts */}
            {deviceInfo && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">2. Scan Parts (QR Code)</h2>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={scannedPart}
                    onChange={(e) => setScannedPart(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleScanPart()}
                    placeholder="Scan part QR code..."
                    className="flex-1 px-4 py-3 border-2 rounded-lg"
                  />
                  <button
                    onClick={handleScanPart}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  QR Format: PartCode+Supplier+Price+PONo+GRNDate
                </p>
              </div>
            )}
          </div>

          {/* Right: Scanned Parts List */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Scanned Parts ({parts.length})
            </h2>

            {parts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p>No parts scanned yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {parts.map((part, index) => (
                  <div
                    key={index}
                    className={`p-4 border-2 rounded-lg ${getActionColor(part.action)}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900">{part.part_code}</div>
                        <div className="text-xs text-gray-600">
                          Supplier: {part.supplier} | Price: ₹{part.purchase_price}
                        </div>
                        <div className="text-xs text-gray-500">
                          PO: {part.po_no} | GRN: {part.grn_date.substring(0, 10)}
                        </div>
                      </div>
                      <button
                        onClick={() => removePart(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Action Selector */}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => updatePartAction(index, 'consume')}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                          part.action === 'consume'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Consume
                      </button>
                      <button
                        onClick={() => updatePartAction(index, 'return')}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                          part.action === 'return'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Return
                      </button>
                      <button
                        onClick={() => updatePartAction(index, 'damaged')}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                          part.action === 'damaged'
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Damaged
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            {parts.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {parts.filter(p => p.action === 'consume').length}
                    </div>
                    <div className="text-xs text-gray-600">To Consume</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {parts.filter(p => p.action === 'return').length}
                    </div>
                    <div className="text-xs text-gray-600">To Return</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">
                      {parts.filter(p => p.action === 'damaged').length}
                    </div>
                    <div className="text-xs text-gray-600">Damaged</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        {parts.length > 0 && deviceInfo && (
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-4 bg-blue-600 text-white rounded-lg font-bold text-lg hover:bg-blue-700 disabled:bg-gray-300"
            >
              {loading ? 'Recording...' : 'Record Parts Consumption'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartsConsumption;
